/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import { EuiBasicTable, EuiDescribedFormGroup, EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiFormField, CuiFormSwitch } from '@modules/cui/forms'
import type { SamlAttributeSettings } from '@modules/cloud-api/v1/types'
import { DocLink } from '@modules/doclinks'

import { labels, messages, sections } from '../../authProviderMessages'

import type { FunctionComponent } from 'react'

interface Props {
  hideOptionalAttributes: boolean
  setFieldValue: (key: string, value: any) => void
}

const AttributeMappings: FunctionComponent<Props> = ({ setFieldValue, hideOptionalAttributes }) => (
  <EuiDescribedFormGroup
    title={
      <h3>
        <FormattedMessage {...sections.attributeMappings} />
      </h3>
    }
    description={
      <Fragment>
        <EuiText size='s'>
          <FormattedMessage
            {...sections.attributeMappingsDescription}
            values={{
              learnMore: (
                <DocLink link='samlProviderAttributeMappingDocLink'>
                  <FormattedMessage {...messages.learnMore} />
                </DocLink>
              ),
            }}
          />
        </EuiText>

        <EuiSpacer size='m' />

        <CuiFormSwitch
          name='hide_optional_attributes'
          label={<FormattedMessage {...labels.hideOptionalAttributes} />}
          setFieldValue={setFieldValue}
        />
      </Fragment>
    }
  >
    <EuiBasicTable
      items={items.filter((item) => !hideOptionalAttributes || !item.optional)}
      columns={[
        {
          field: `name`,
          name: 'User property',
        },
        {
          name: 'SAML attribute name',
          render: ({ name }: SamlAtributeMapping) => (
            <CuiFormField
              name={`attribute_mappings.${name}`}
              aria-label={`SAML attribute name mapping for user property ${name}`}
            />
          ),
        },
      ]}
    />
  </EuiDescribedFormGroup>
)

type SamlAtributeMapping = {
  name: keyof SamlAttributeSettings
  optional?: boolean
}

const items: SamlAtributeMapping[] = [
  { name: `principal` },
  { name: `groups` },
  { name: `name`, optional: true },
  { name: `mail`, optional: true },
  { name: `dn`, optional: true },
]

export default AttributeMappings
